import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionButton from '../../components/ui/QuickActionButton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CameraViewfinder from './components/CameraViewfinder';

import VehicleDataCard from './components/VehicleDataCard';
import ScanHistory from './components/ScanHistory';
import OfflineIndicator from './components/OfflineIndicator';
import QuickActions from './components/QuickActions';
import CreateOrderModal from './components/CreateOrderModal';
import vinService from '../../services/vinService';
import { orderService } from '../../services/orderService';
import { vehicleService } from '../../services/vehicleService';
import scanHistoryService from '../../services/scanHistoryService';
import { fahrtenService } from '../../services/fahrtenService';
import { useAuth } from '../../contexts/AuthContext';

const VinScanner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isScanning, setIsScanning] = useState(true); // Start scanning by default
  const [vehicleData, setVehicleData] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingSyncs, setPendingSyncs] = useState(0);
  const [activeTab, setActiveTab] = useState('camera');

  // Error state for API issues
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load scan history from database
    const loadScanHistory = async () => {
      if (user?.id) {
        try {
          const { data, error } = await scanHistoryService.getScanHistory(user.id);
          if (error) {
            console.error('Error loading scan history:', error);
            setScanHistory([]);
          } else {
            // Transform database data to match the expected format
            const transformedHistory = data?.map(scan => ({
              id: scan.id,
              vin: scan.vin,
              make: scan.make,
              model: scan.model,
              year: scan.year,
              engine: scan.engine,
              transmission: scan.transmission,
              fuelType: scan.fuel_type,
              bodyType: scan.body_type,
              color: scan.color,
              doors: scan.doors,
              displacement: scan.displacement,
              power: scan.power,
              cylinders: scan.cylinders,
              driveType: scan.drive_type,
              trim: scan.trim,
              series: scan.series,
              country: scan.country,
              plant: scan.plant,
              capturedImage: scan.captured_image,
              timestamp: scan.created_at,
              hasOrder: scan.has_order,
              source: scan.source,
              isUnknown: scan.is_unknown,
              error: scan.error_message
            })) || [];

            setScanHistory(transformedHistory);
          }
        } catch (error) {
          console.error('Error loading scan history:', error);
          setScanHistory([]);
        }
      }
    };

    loadScanHistory();

    // Setup online/offline listeners
    const handleOnline = () => {
      setIsOffline(false);
      syncPendingData();
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user?.id]);

  const syncPendingData = () => {
    // This function is no longer needed since we're using the database
    // Keeping it for potential future offline functionality
    setPendingSyncs(0);
  };

  const handleVinDetected = async (vin, capturedImage = null) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Validate VIN format
      if (!vinService.validateVin(vin)) {
        throw new Error('Ungültiges VIN-Format. VIN muss 17 Zeichen lang sein und darf keine I, O oder Q enthalten.');
      }

      // Check cache first
      let vehicleInfo = vinService.getCachedVinData(vin);
      
      if (!vehicleInfo) {
        // Call Vincario API
        vehicleInfo = await vinService.decodeVin(vin);
        
        // Cache the result
        vinService.cacheVinData(vin, vehicleInfo);
      }

      // Add captured image and timestamp
      const enrichedData = {
        ...vehicleInfo,
        capturedImage,
        timestamp: new Date()?.toISOString()
      };
      
      setVehicleData(enrichedData);
      
      // Save scan to database
      if (user?.id) {
        try {
          const { data: savedScan, error: saveError } = await scanHistoryService.saveScan(enrichedData, user.id);
          if (saveError) {
            console.error('Error saving scan to database:', saveError);
          } else {
            // Add to local state with database ID
            const scanWithId = { ...enrichedData, id: savedScan.id };
            const newHistory = [scanWithId, ...scanHistory?.slice(0, 9)];
            setScanHistory(newHistory);

            // Automatically create a fahrt entry
            try {
              const currentDate = new Date();
              const fahrtPayload = {
                vin: enrichedData.vin,
                vehicle_make: enrichedData.make || 'Unbekannt',
                vehicle_model: enrichedData.model || 'Unbekannt',
                fahrt_datum: currentDate.toISOString().split('T')[0], // Current date
                fahrt_uhrzeit_beginn: currentDate.toTimeString().slice(0, 5), // Current time
                fahrt_uhrzeit_ende: null, // No end time initially
                startziel: '', // Empty initially
                ueber: '', // Empty initially
                endziel: '', // Empty initially
                fahrtzweck: 'VIN-Scan', // Default purpose for auto-created fahrten
                fahrer_name: '', // Empty initially
                fahrer_adresse: '', // Empty initially
                notes: `Automatisch erstellt beim VIN-Scan am ${currentDate.toLocaleString('de-DE')}`,
                status: 'pending',
                metadata: {
                  created_via: 'auto_vin_scan',
                  scanned_vin: enrichedData.vin,
                  original_vehicle_data: enrichedData,
                  auto_created: true
                }
              };

              const { data: createdFahrt, error: fahrtError } = await fahrtenService.createFahrt(fahrtPayload, user.id);
              
              if (fahrtError) {
                console.error('Error auto-creating fahrt:', fahrtError);
              } else {
                console.log('✅ Auto-created fahrt for VIN:', enrichedData.vin);
                
                // Update scan history to mark as having order
                await scanHistoryService.updateScanOrderStatus(enrichedData.vin, user.id, true);
                
                // Update local scan history
                const updatedHistory = scanHistory?.map(scan => 
                  scan?.vin === enrichedData?.vin ? { ...scan, hasOrder: true } : scan
                );
                setScanHistory(updatedHistory);
              }
            } catch (autoFahrtError) {
              console.error('Error in auto-fahrt creation:', autoFahrtError);
              // Don't throw error - scan was successful, auto-fahrt is optional
            }
          }
        } catch (error) {
          console.error('Error saving scan:', error);
        }
      }
      
      setActiveTab('result');
    } catch (error) {
      console.error('VIN processing error:', error);
      setError(error.message);
      
      // Handle unknown VIN
      const unknownVehicle = {
        vin,
        make: 'Unbekannt',
        model: 'Fahrzeugdaten nicht gefunden',
        year: 'N/A',
        capturedImage,
        timestamp: new Date()?.toISOString(),
        isUnknown: true,
        error: error.message
      };
      setVehicleData(unknownVehicle);
      
      // Save unknown VIN scan to database and create auto-fahrt
      if (user?.id) {
        try {
          const { data: savedScan, error: saveError } = await scanHistoryService.saveScan(unknownVehicle, user.id);
          if (saveError) {
            console.error('Error saving unknown VIN scan to database:', saveError);
          } else {
            // Add to local state with database ID
            const scanWithId = { ...unknownVehicle, id: savedScan.id };
            const newHistory = [scanWithId, ...scanHistory?.slice(0, 9)];
            setScanHistory(newHistory);

            // Automatically create a fahrt entry for unknown VIN
            try {
              const currentDate = new Date();
              const fahrtPayload = {
                vin: unknownVehicle.vin,
                vehicle_make: 'Unbekannt',
                vehicle_model: 'Fahrzeugdaten nicht gefunden',
                fahrt_datum: currentDate.toISOString().split('T')[0], // Current date
                fahrt_uhrzeit_beginn: currentDate.toTimeString().slice(0, 5), // Current time
                fahrt_uhrzeit_ende: null, // No end time initially
                startziel: '', // Empty initially
                ueber: '', // Empty initially
                endziel: '', // Empty initially
                fahrtzweck: 'VIN-Scan', // Default purpose for auto-created fahrten
                fahrer_name: '', // Empty initially
                fahrer_adresse: '', // Empty initially
                notes: `Automatisch erstellt beim VIN-Scan (unbekanntes Fahrzeug) am ${currentDate.toLocaleString('de-DE')}`,
                status: 'pending',
                metadata: {
                  created_via: 'auto_vin_scan_unknown',
                  scanned_vin: unknownVehicle.vin,
                  original_vehicle_data: unknownVehicle,
                  auto_created: true,
                  is_unknown_vehicle: true
                }
              };

              const { data: createdFahrt, error: fahrtError } = await fahrtenService.createFahrt(fahrtPayload, user.id);
              
              if (fahrtError) {
                console.error('Error auto-creating fahrt for unknown VIN:', fahrtError);
              } else {
                console.log('✅ Auto-created fahrt for unknown VIN:', unknownVehicle.vin);
                
                // Update scan history to mark as having order
                await scanHistoryService.updateScanOrderStatus(unknownVehicle.vin, user.id, true);
                
                // Update local scan history
                const updatedHistory = scanHistory?.map(scan => 
                  scan?.vin === unknownVehicle?.vin ? { ...scan, hasOrder: true } : scan
                );
                setScanHistory(updatedHistory);
              }
            } catch (autoFahrtError) {
              console.error('Error in auto-fahrt creation for unknown VIN:', autoFahrtError);
              // Don't throw error - scan was successful, auto-fahrt is optional
            }
          }
        } catch (error) {
          console.error('Error saving unknown VIN scan:', error);
        }
      }
      
      setActiveTab('result');
    } finally {
      setIsLoading(false);
      setIsScanning(false);
    }
  };

  const handleCreateOrder = () => {
    if (!vehicleData) return;
    setIsOrderModalOpen(true);
  };

  const handleOrderSubmit = async (orderData) => {
    setIsCreatingOrder(true);
    
    try {
      if (!user?.id) {
        throw new Error('Benutzer nicht angemeldet');
      }

      // Prepare fahrt data for the new fahrten table
      const fahrtPayload = {
        vin: vehicleData.vin,
        vehicle_make: vehicleData.make,
        vehicle_model: vehicleData.model,
        fahrt_datum: new Date().toISOString().split('T')[0], // Current date
        fahrt_uhrzeit_beginn: new Date().toTimeString().slice(0, 5), // Current time
        fahrt_uhrzeit_ende: orderData.fahrtEnde || null, // End time from form
        startziel: orderData.startziel || '',
        ueber: orderData.ueber || '',
        endziel: orderData.endziel || '',
        fahrtzweck: orderData.fahrtzweck || '',
        fahrer_name: orderData.fahrerName || '',
        fahrer_adresse: orderData.fahrerAdresse || '',
        notes: orderData.notes || '',
        status: 'pending',
        metadata: {
          created_via: 'vin_scanner',
          scanned_vin: vehicleData.vin,
          original_vehicle_data: vehicleData
        }
      };

      // Create the fahrt entry
      const { data: createdFahrt, error: fahrtError } = await fahrtenService.createFahrt(fahrtPayload, user.id);
      
      if (fahrtError) {
        throw new Error(`Fehler beim Erstellen des Fahrtennachweises: ${fahrtError.message}`);
      }

      console.log('Fahrt created successfully:', createdFahrt);
      
      // Update scan history to mark as having order
      if (user?.id) {
        try {
          await scanHistoryService.updateScanOrderStatus(vehicleData.vin, user.id, true);
        } catch (error) {
          console.error('Error updating scan order status:', error);
        }
      }
      
      const updatedHistory = scanHistory?.map(scan => 
        scan?.vin === vehicleData?.vin ? { ...scan, hasOrder: true } : scan
      );
      setScanHistory(updatedHistory);
      
      alert('Fahrtennachweis erfolgreich erstellt!');
      
      // Close modal and navigate to order management
      setIsOrderModalOpen(false);
      navigate('/order-management-dashboard', {
        state: { newFahrt: createdFahrt }
      });
    } catch (error) {
      console.error('Fahrt creation error:', error);
      alert(`Fehler beim Erstellen des Fahrtennachweises: ${error.message}`);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleSaveVin = () => {
    if (!vehicleData) return;
    
    // VIN is already saved in history, just show confirmation
    alert('VIN wurde erfolgreich gespeichert!');
  };

  const handleNewScan = () => {
    setVehicleData(null);
    setActiveTab('camera');
    setIsScanning(true);
  };

  const refreshScanHistory = async () => {
    if (user?.id) {
      try {
        const { data, error } = await scanHistoryService.getScanHistory(user.id);
        if (error) {
          console.error('Error refreshing scan history:', error);
        } else {
          // Transform database data to match the expected format
          const transformedHistory = data?.map(scan => ({
            id: scan.id,
            vin: scan.vin,
            make: scan.make,
            model: scan.model,
            year: scan.year,
            engine: scan.engine,
            transmission: scan.transmission,
            fuelType: scan.fuel_type,
            bodyType: scan.body_type,
            color: scan.color,
            doors: scan.doors,
            displacement: scan.displacement,
            power: scan.power,
            cylinders: scan.cylinders,
            driveType: scan.drive_type,
            trim: scan.trim,
            series: scan.series,
            country: scan.country,
            plant: scan.plant,
            capturedImage: scan.captured_image,
            timestamp: scan.created_at,
            hasOrder: scan.has_order,
            source: scan.source,
            isUnknown: scan.is_unknown,
            error: scan.error_message
          })) || [];

          setScanHistory(transformedHistory);
        }
      } catch (error) {
        console.error('Error refreshing scan history:', error);
      }
    }
  };

  const handleSelectHistoryVin = (scan) => {
    setVehicleData(scan);
    setActiveTab('result');
  };

  // Refresh scan history when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user?.id) {
        refreshScanHistory();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.id]);

  const handleClearHistory = async () => {
    if (confirm('Möchten Sie den gesamten Scan-Verlauf löschen?')) {
      try {
        if (user?.id) {
          const { error } = await scanHistoryService.clearScanHistory(user.id);
          if (error) {
            console.error('Error clearing scan history:', error);
            alert('Fehler beim Löschen des Scan-Verlaufs');
            return;
          }
        }
        setScanHistory([]);
        setPendingSyncs(0);
      } catch (error) {
        console.error('Error clearing scan history:', error);
        alert('Fehler beim Löschen des Scan-Verlaufs');
      }
    }
  };

  const handleDeleteSelectedScans = async (selectedScanIds) => {
    if (selectedScanIds.length === 0) return;
    
    const confirmMessage = selectedScanIds.length === 1 
      ? 'Möchten Sie diesen Scan-Eintrag löschen?'
      : `Möchten Sie ${selectedScanIds.length} Scan-Einträge löschen?`;
    
    if (confirm(confirmMessage)) {
      try {
        if (user?.id) {
          // Delete each selected scan
          for (const scanId of selectedScanIds) {
            const { error } = await scanHistoryService.deleteScan(scanId, user.id);
            if (error) {
              console.error('Error deleting scan:', error);
            }
          }
        }
        
        // Update local state by removing deleted scans
        setScanHistory(prevHistory => 
          prevHistory.filter(scan => !selectedScanIds.includes(scan.id))
        );
        
        alert(`${selectedScanIds.length} Scan-Einträge erfolgreich gelöscht!`);
      } catch (error) {
        console.error('Error deleting selected scans:', error);
        alert('Fehler beim Löschen der ausgewählten Scan-Einträge');
      }
    }
  };

  const tabs = [
    { id: 'camera', label: 'Kamera-Scan', icon: 'Camera' },
    { id: 'result', label: 'Ergebnis', icon: 'CheckCircle', disabled: !vehicleData },
    { id: 'history', label: 'Verlauf', icon: 'History' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" />
             {/* Hide sidebar on mobile */}
       <div className="hidden lg:block">
         <Sidebar 
           isCollapsed={isSidebarCollapsed}
           onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
           userRole="admin"
         />
       </div>
      <OfflineIndicator isOffline={isOffline} pendingSyncs={pendingSyncs} />
      <main className={`pt-16 pb-20 lg:pb-8 transition-all duration-200 ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">VIN Scanner</h1>
                <p className="text-muted-foreground">
                  Scannen Sie Fahrzeugidentifikationsnummern und erstellen Sie Serviceaufträge
                </p>
              </div>
              
              <div className="hidden lg:flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/order-management-dashboard')}
                  iconName="ClipboardList"
                  iconPosition="left"
                >
                  Fahrten
                </Button>
                <Button
                  variant="default"
                  onClick={handleNewScan}
                  iconName="Scan"
                  iconPosition="left"
                >
                  Neuer Scan
                </Button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => !tab?.disabled && setActiveTab(tab?.id)}
                    disabled={tab?.disabled}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary'
                        : tab?.disabled
                        ? 'border-transparent text-muted-foreground cursor-not-allowed opacity-50'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center">
                <Icon name="AlertTriangle" size={20} className="text-error mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-error">API-Fehler</h4>
                  <p className="text-sm text-error/80 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'camera' && (
              <div className="space-y-6">
                <CameraViewfinder
                  onVinDetected={handleVinDetected}
                  isScanning={isScanning}
                  onToggleCamera={() => setIsScanning(!isScanning)}
                />
                
                <div className="lg:hidden">
                  <QuickActions
                    vehicleData={vehicleData}
                    onNewScan={handleNewScan}
                  />
                </div>
              </div>
            )}



            {activeTab === 'result' && vehicleData && (
              <div className="space-y-6">
                <VehicleDataCard
                  vehicleData={vehicleData}
                  onCreateOrder={handleCreateOrder}
                  onSaveVin={handleSaveVin}
                  isCreatingOrder={isCreatingOrder}
                />
                
                <div className="lg:hidden">
                  <QuickActions
                    vehicleData={vehicleData}
                    onNewScan={handleNewScan}
                  />
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-6">
                <ScanHistory
                  scanHistory={scanHistory}
                  onSelectVin={handleSelectHistoryVin}
                  onClearHistory={handleClearHistory}
                  onDeleteSelected={handleDeleteSelectedScans}
                />
                
                <div className="lg:hidden">
                  <QuickActions
                    vehicleData={vehicleData}
                    onNewScan={handleNewScan}
                  />
                </div>
              </div>
            )}

            {/* Desktop Quick Actions */}
            <div className="hidden lg:block">
              <QuickActions
                vehicleData={vehicleData}
                onNewScan={handleNewScan}
              />
            </div>
          </div>
        </div>
      </main>
      
      {/* Create Order Modal */}
      <CreateOrderModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        vehicleData={vehicleData}
        onCreateOrder={handleOrderSubmit}
        isLoading={isCreatingOrder}
      />
      
      <QuickActionButton userRole="admin" />
    </div>
  );
};

export default VinScanner;